import { Link } from "react-router-dom";

import { CopyButton } from "@/client/components/copy-button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { API_BASE } from "@/client/lib/api";

/**
 * Surfaces a node's raw Redfish endpoint for automation (PE-937). The
 * interactive Explorer is staff-only; customers get the endpoint URL + a sample
 * request instead, called non-interactively with a customer API token (PE-936)
 * against the pass-through route (PE-935), scoped to nodes they own.
 */
export function RedfishEndpointCard({
  nodeId,
  canManageTokens,
}: {
  nodeId: string;
  canManageTokens: boolean;
}) {
  const endpoint = `${window.location.origin}${API_BASE}/nodes/${nodeId}/redfish/`;
  const sample = `curl -H "Authorization: Bearer $MANTLE_TOKEN" \\\n  ${endpoint}v1/Systems/System_0`;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Redfish API</CardTitle>
        <CardDescription>
          Call this node's Redfish directly from automation, using standard HTTP verbs. Authenticate
          with a customer API token (tenant admins only); access is scoped to your own nodes.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-muted-foreground text-xs font-medium">Endpoint</span>
          <div className="bg-muted relative rounded-md px-2 py-1.5">
            <CopyButton
              value={endpoint}
              label="Copy Redfish endpoint"
              className="absolute top-1/2 right-1.5 -translate-y-1/2"
            />
            <code className="block truncate pr-8 font-mono text-xs">{endpoint}</code>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-muted-foreground text-xs font-medium">Example request</span>
          <div className="bg-muted relative rounded-md p-3">
            <CopyButton
              value={sample}
              label="Copy example request"
              className="absolute top-1.5 right-1.5"
            />
            <pre className="overflow-x-auto pr-8 font-mono text-xs whitespace-pre">{sample}</pre>
          </div>
        </div>

        <p className="text-muted-foreground text-xs">
          {canManageTokens ? (
            <>
              Create a token on the{" "}
              <Link to="/api-tokens" className="text-primary underline underline-offset-4">
                API tokens
              </Link>{" "}
              page.
            </>
          ) : (
            "A tenant admin can create an API token from the API tokens page."
          )}
        </p>
      </CardContent>
    </Card>
  );
}
